import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { activeTenantNamespacedKey as tenantNamespacedKey } from '@auw/tenant';

type RememberedIdByMode = Partial<Record<WorkspaceMode, string | null>>;
type PageSidebarOpenByMode = Partial<Record<WorkspaceMode, boolean>>;

const DEFAULT_PAGE_SIDEBAR_OPEN_BY_MODE: Partial<Record<WorkspaceMode, boolean>> = {
  document: true,
  storage: true,
  message: true,
  // 구매 에이전트 — 목록 패널이 곧 선적 건 현황판이라 기본 열림.
  // 접힘이 기본이면 상태(승인 대기 ⏸ 등)가 아예 안 보인다.
  'purchase-agent': true,
  // 사용자 가이드 — 패널이 곧 문서 목차라 기본 열림. 접힌 채로 들어오면
  // 어떤 기능들의 설명이 있는지 알 방법이 없다.
  'user-guide': true,
};

function getDefaultPageSidebarOpen(mode: WorkspaceMode): boolean {
  return DEFAULT_PAGE_SIDEBAR_OPEN_BY_MODE[mode] ?? false;
}

type ComposerSuppressedByMode = Partial<Record<WorkspaceMode, boolean>>;

interface WorkspaceNavigationState {
  rememberedIdByMode: RememberedIdByMode;
  pageSidebarOpenByMode: PageSidebarOpenByMode;
  /**
   * 모드별 composer(메시지 입력창) 일시 억제 상태.
   *
   * `WORKSPACE_MODES[mode].showInput` 은 모드마다 고정값이라 "화면 상태에 따라
   * 보였다 숨었다" 를 표현할 수 없다. GTC 에이전트는 분석 **전** 시작 페이지에서
   * 입력창이 필요 없고 분석 **후** 후속 채팅에서만 필요하므로, 페이지가 이
   * 플래그로 억제 여부를 알린다.
   *
   * 기본값은 `false`(억제 안 함) 라 이 플래그를 쓰지 않는 기존 모드는 무영향이다.
   * 화면 상태라 persist 하지 않는다 — 새로고침하면 페이지가 다시 계산한다.
   */
  composerSuppressedByMode: ComposerSuppressedByMode;
  rememberModeSelection: (mode: WorkspaceMode, conversationId: string | null) => void;
  clearRememberedModeSelection: (mode: WorkspaceMode) => void;
  isPageSidebarOpen: (mode: WorkspaceMode) => boolean;
  setPageSidebarOpen: (mode: WorkspaceMode, open: boolean) => void;
  togglePageSidebar: (mode: WorkspaceMode) => void;
  isComposerSuppressed: (mode: WorkspaceMode) => boolean;
  setComposerSuppressed: (mode: WorkspaceMode, suppressed: boolean) => void;
  resetWorkspaceNavigation: () => void;
}

export const useWorkspaceNavigationStore = create<WorkspaceNavigationState>()(
  persist(
    (set, get) => ({
      rememberedIdByMode: {},
      pageSidebarOpenByMode: {},
      composerSuppressedByMode: {},
      rememberModeSelection: (mode, conversationId) => {
        set((state) => ({
          rememberedIdByMode: {
            ...state.rememberedIdByMode,
            [mode]: conversationId,
          },
        }));
      },
      clearRememberedModeSelection: (mode) => {
        set((state) => ({
          rememberedIdByMode: {
            ...state.rememberedIdByMode,
            [mode]: null,
          },
        }));
      },
      isPageSidebarOpen: (mode) => get().pageSidebarOpenByMode[mode] ?? getDefaultPageSidebarOpen(mode),
      setPageSidebarOpen: (mode, open) => {
        set((state) => ({
          pageSidebarOpenByMode: {
            ...state.pageSidebarOpenByMode,
            [mode]: open,
          },
        }));
      },
      togglePageSidebar: (mode) => {
        set((state) => ({
          pageSidebarOpenByMode: {
            ...state.pageSidebarOpenByMode,
            [mode]: !(state.pageSidebarOpenByMode[mode] ?? getDefaultPageSidebarOpen(mode)),
          },
        }));
      },
      isComposerSuppressed: (mode) => get().composerSuppressedByMode[mode] ?? false,
      setComposerSuppressed: (mode, suppressed) => {
        set((state) => (
          (state.composerSuppressedByMode[mode] ?? false) === suppressed
            ? state                               // 같은 값이면 리렌더 유발하지 않는다
            : {
              composerSuppressedByMode: {
                ...state.composerSuppressedByMode,
                [mode]: suppressed,
              },
            }
        ));
      },
      resetWorkspaceNavigation: () => {
        set({
          rememberedIdByMode: {},
          pageSidebarOpenByMode: {},
          composerSuppressedByMode: {},
        });
      },
    }),
    {
      name: tenantNamespacedKey('workspace-navigation'),
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        pageSidebarOpenByMode: state.pageSidebarOpenByMode,
      }),
    },
  ),
);
